import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Lock } from "lucide-react";
import { ReactionsList } from "@/components/ReactionsList";
import { floatEmojis, confetti } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { useIsVip } from "@/lib/vip";
import { SP_HEART, SpHeart, ReactionGlyph } from "@/components/SpHeart";
import { isHalloween } from "@/lib/halloween";

export const REACTIONS = [["❤️", "Gefällt mir"], ["😂", "Haha"], ["😮", "Wow"], ["😢", "Traurig"], ["😡", "Wütend"], ["👍", "Daumen hoch"]];
const HOLD_MS = 450;

function usePicker(onReact) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null), held = useRef(false), leave = useRef(null), hover = useRef(null);
  const start = () => { clearTimeout(hover.current); held.current = false; timer.current = setTimeout(() => { held.current = true; setOpen(true); }, HOLD_MS); };
  const end = () => clearTimeout(timer.current);
  const tap = () => { if (held.current) { held.current = false; return; } setOpen(false); onReact(null); };
  const pick = (e) => { setOpen(false); onReact(e); };
  const hoverIn = () => { clearTimeout(leave.current); if (window.matchMedia("(hover: hover)").matches) hover.current = setTimeout(() => setOpen(true), 500); };
  const hoverOut = () => { clearTimeout(hover.current); clearTimeout(timer.current); leave.current = setTimeout(() => setOpen(false), 300); };
  return { open, setOpen, start, end, tap, pick, hoverIn, hoverOut };
}

function SpHeartOption({ id, mine, pick, vip }) {
  const nav = useNavigate();
  const locked = () => toast("Das Rosé-Gold-Herz ist nur für Projekt Unterstützer 💖", { description: "Unterstütze die Community und verschenke leuchtende Herzen.", action: { label: "Unterstützen", onClick: () => nav("/community-unterstuetzen") } });
  return (
    <button type="button" onClick={vip ? () => pick(SP_HEART) : locked} title="Rosé-Gold-Herz" aria-label={vip ? "Rosé-Gold-Herz" : "Rosé-Gold-Herz (nur für Projekt Unterstützer)"} data-testid={`reaction-${id}-sp-heart`} data-locked={!vip || undefined}
      className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-transform ${vip ? "hover:scale-125 hover:-translate-y-1 sp-heart-option" : "opacity-60 grayscale-[.4]"} ${mine === SP_HEART ? "bg-rose-50" : ""}`}>
      <SpHeart className="w-7 h-7" />
      {!vip && <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-slate-700 text-white flex items-center justify-center ring-2 ring-white"><Lock className="w-2.5 h-2.5" /></span>}
    </button>
  );
}

const HW_STICKERS = [["🎃", "Kürbis"], ["👻", "Geist"], ["🦇", "Fledermaus"], ["💀", "Totenkopf"], ["🕷️", "Spinne"]];

const Picker = ({ id, mine, pick, small, withSp, vip }) => (
  <div className={`absolute bottom-full left-0 mb-2 z-30 p-1.5 rounded-[1.5rem] bg-white shadow-xl shadow-rose-200/60 border border-pink-100 fade-up`} data-testid={`reaction-picker-${id}`}>
    <div className="flex gap-0.5">
    {REACTIONS.map(([e, l], i) => (
      <button key={e} type="button" onClick={() => pick(e)} title={l} aria-label={l} data-testid={`reaction-${id}-${i}`}
        className={`${small ? "w-9 h-9 text-xl" : "w-10 h-10 text-2xl"} rounded-full flex items-center justify-center hover:scale-125 hover:-translate-y-1 transition-transform ${mine === e ? "bg-rose-50" : ""}`}>{e}</button>
    ))}
    {withSp && <SpHeartOption id={id} mine={mine} pick={pick} vip={vip} />}
    </div>
    {withSp && isHalloween() && (
      <div className="mt-1 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-[#2a0f45] to-[#4c1d95] px-1.5" data-testid={`reaction-${id}-halloween`}>
        <span className="px-1.5 text-[10px] font-black uppercase tracking-wider text-orange-300">Grusel</span>
        {HW_STICKERS.map(([e, l]) => (
          <button key={e} type="button" onClick={() => pick(e)} title={l} aria-label={l} data-testid={`reaction-${id}-hw-${l}`}
            className={`w-9 h-9 text-xl rounded-full flex items-center justify-center hover:scale-125 hover:-translate-y-1 transition-transform ${mine === e ? "bg-white/20" : ""}`}>{e}</button>
        ))}
      </div>
    )}
  </div>
);

function spBurst(el) {
  floatEmojis(el, SP_HEART, 10);
  const r = el?.getBoundingClientRect();
  if (r) confetti({ x: r.left + r.width / 2, y: r.top, count: 36, spread: 1.2, colors: ["#f9a8c9", "#ec4899", "#e6b85c", "#fde68a", "#fff"] });
}

export function ReactionButton({ p, onReact, className, kind = "post", small = false }) {
  const btn = useRef(null);
  const { user } = useAuth();
  const vip = useIsVip(user?.user_id);
  const pk = usePicker((e) => { if (e === SP_HEART && p.my_reaction !== SP_HEART) spBurst(btn.current); else if (e || !p.liked) floatEmojis(btn.current, e || "❤️", small ? 5 : 8); onReact(e); });
  const [list, setList] = useState(false);
  const mine = p.my_reaction;
  const others = (p.top_reactions || []).filter((e) => e !== (mine || "❤️"));
  const pre = kind === "post" ? "post" : "post-comment";
  return (
    <div className="relative flex items-center">
      <div className="relative flex" onMouseEnter={pk.hoverIn} onMouseLeave={pk.hoverOut}>
      {pk.open && <Picker id={p.id} mine={mine} pick={pk.pick} small={small} withSp={kind === "post"} vip={vip} />}
      <button ref={btn} type="button" onClick={pk.tap} onPointerDown={pk.start} onPointerUp={pk.end} onPointerLeave={pk.end} onContextMenu={(e) => e.preventDefault()}
        data-testid={`${pre}-like-${p.id}`} aria-label="Reagieren" className={`${className} relative z-20 select-none ${mine === SP_HEART ? "sp-heart-active" : ""} ${p.liked ? "text-rose-600" : "text-slate-500 hover:bg-rose-50"}`} style={{ WebkitTouchCallout: "none" }}>
        {mine && mine !== "❤️"
          ? <span className={`${small ? "text-sm leading-none" : "text-lg leading-none"} inline-flex`} data-testid={`${pre}-my-reaction-${p.id}`}><ReactionGlyph e={mine} className={small ? "w-4 h-4" : "w-6 h-6"} /></span>
          : <Heart className={`${small ? "w-3.5 h-3.5" : "w-5 h-5"} transition-transform active:scale-125 ${p.liked ? "fill-rose-500" : ""}`} />}
        {small && <span>{p.liked ? "Gefällt dir" : "Gefällt mir"}</span>}
      </button>
      </div>
      {p.likes > 0 && (
        <button type="button" onClick={() => setList(true)} data-testid={`${pre}-reactions-open-${p.id}`} title="Wer hat reagiert?"
          className={`relative z-20 inline-flex items-center gap-1 rounded-full font-semibold hover:underline ${small ? "text-[11px] text-slate-400 ml-1" : "text-sm text-slate-500 -ml-1.5 pr-2 h-9"}`}>
          {others.length > 0 && <span className="flex -space-x-1" data-testid={`${pre}-top-reactions-${p.id}`}>{others.map((e) => <span key={e} className="inline-flex items-center"><ReactionGlyph e={e} className="w-[18px] h-[18px]" /></span>)}</span>}
          <span data-testid={`${pre}-like-count-${p.id}`}>{p.likes}</span>
        </button>
      )}
      {p.likes === 0 && !small && <span className="text-sm font-semibold text-slate-500 -ml-1.5 pr-2" data-testid={`${pre}-like-count-${p.id}`}>0</span>}
      {pk.open && <button type="button" aria-label="Schließen" className="fixed inset-0 z-10 cursor-default" onClick={() => pk.setOpen(false)} />}
      {list && <ReactionsList kind={kind} id={p.id} onClose={() => setList(false)} />}
    </div>
  );
}
